import { MenuItem } from 'primeng/api';
import { WarehousingMasterData } from './warehousing.model';

export type WarehousingSection =
  | 'overview'
  | 'items'
  | 'stock-entry'
  | 'stock'
  | 'stock-ledger'
  | 'quality'
  | 'reservations'
  | 'dispatch'
  | 'shipment-work'
  | 'transfers'
  | 'refurbishment'
  | 'destruction'
  | 'setup'
  | 'billing-drafts';

export interface WarehousingNavItem {
  label: string;
  icon: string;
  route: string;
  section: WarehousingSection;
}

export type WarehousingMasterType =
  | 'REGION'
  | 'WAREHOUSE'
  | 'ZONE'
  | 'ITEM_CONDITION'
  | 'TENANT_UOM'
  | 'CATEGORY'
  | 'BRAND'
  | 'SUB_BRAND';

export const WAREHOUSING_MASTER_TYPES: WarehousingMasterType[] = [
  'REGION',
  'WAREHOUSE',
  'ZONE',
  'ITEM_CONDITION',
  'TENANT_UOM',
  'CATEGORY',
  'BRAND',
  'SUB_BRAND'
];

export const WAREHOUSING_MASTER_TYPE_LABELS: Record<WarehousingMasterType, string> = {
  REGION: 'Regions',
  WAREHOUSE: 'Warehouses',
  ZONE: 'Zones',
  ITEM_CONDITION: 'Item Conditions',
  TENANT_UOM: 'Tenant UOMs',
  CATEGORY: 'Categories',
  BRAND: 'Brands',
  SUB_BRAND: 'Sub Brands'
};

export const WAREHOUSING_MASTER_TYPE_DESCRIPTIONS: Record<WarehousingMasterType, string> = {
  REGION: 'Maintain warehouse operating regions used for storage, stock, and dispatch.',
  WAREHOUSE: 'Maintain physical warehouses and their linked operating locations.',
  ZONE: 'Maintain warehouse zones under each warehouse for stock placement.',
  ITEM_CONDITION: 'Maintain stock condition labels used for receiving, storage, and dispatch.',
  TENANT_UOM: 'Maintain tenant-owned units of measure used by warehouse items and stock operations.',
  CATEGORY: 'Maintain client and account scoped item categories with child category levels.',
  BRAND: 'Maintain brands under item categories.',
  SUB_BRAND: 'Maintain sub brands under brands.'
};

export const WAREHOUSING_MASTER_TYPE_ROUTES: Record<WarehousingMasterType, string> = {
  REGION: '/setup/regions',
  WAREHOUSE: '/setup/warehouses',
  ZONE: '/warehousing/setup/zones',
  ITEM_CONDITION: '/warehousing/setup/item-conditions',
  TENANT_UOM: '/warehousing/setup/uoms',
  CATEGORY: '/warehousing/setup/categories',
  BRAND: '/warehousing/setup/brands',
  SUB_BRAND: '/warehousing/setup/sub-brands'
};

export const WAREHOUSING_NAV_ITEMS: WarehousingNavItem[] = [
  { label: 'Overview', icon: 'pi pi-chart-pie', route: '/warehousing/overview', section: 'overview' },
  { label: 'Items', icon: 'pi pi-box', route: '/warehousing/items', section: 'items' },
  { label: 'Goods In', icon: 'pi pi-sign-in', route: '/warehousing/stock-entry', section: 'stock-entry' },
  { label: 'On Hand Stock', icon: 'pi pi-warehouse', route: '/warehousing/stock', section: 'stock' },
  { label: 'Item Transactions Ledger', icon: 'pi pi-history', route: '/warehousing/stock-ledger', section: 'stock-ledger' },
  { label: 'Quality Lots', icon: 'pi pi-shield', route: '/warehousing/quality', section: 'quality' },
  { label: 'Reservations', icon: 'pi pi-inbox', route: '/warehousing/reservations', section: 'reservations' },
  { label: 'Goods Out & Dispatches', icon: 'pi pi-send', route: '/warehousing/dispatch', section: 'dispatch' },
  { label: 'Outbound Work', icon: 'pi pi-sign-out', route: '/warehousing/shipment-work/outbound', section: 'shipment-work' },
  { label: 'Inbound Work', icon: 'pi pi-sign-in', route: '/warehousing/shipment-work/inbound', section: 'shipment-work' },
  { label: 'Transfers', icon: 'pi pi-sync', route: '/warehousing/transfers', section: 'transfers' },
  { label: 'Refurbishment', icon: 'pi pi-wrench', route: '/warehousing/refurbishment', section: 'refurbishment' },
  { label: 'Destruction', icon: 'pi pi-trash', route: '/warehousing/destruction', section: 'destruction' },
  { label: 'Setup', icon: 'pi pi-cog', route: '/warehousing/setup/storage-rates', section: 'setup' },
  { label: 'Billing Drafts', icon: 'pi pi-file-edit', route: '/warehousing/billing-drafts', section: 'billing-drafts' }
];

export const TRACKING_MODES = [
  { label: 'Quantity', value: 'QUANTITY' },
  { label: 'Unit-level tracking required', value: 'UNIT' }
];

export const BILLING_CHARGE_UNITS = [
  { label: 'SQM by day', value: 'SQM' },
  { label: 'CBM by day', value: 'CBM' }
];

export const BILLING_CHARGE_TYPES = [
  { label: 'Storage', value: 'STORAGE' },
  { label: 'Handling In', value: 'HANDLING_IN' },
  { label: 'Handling Out', value: 'HANDLING_OUT' },
  { label: 'Picking', value: 'PICKING' },
  { label: 'Packing', value: 'PACKING' },
  { label: 'Labeling', value: 'LABELING' },
  { label: 'Palletization', value: 'PALLETIZATION' },
  { label: 'Depalletization', value: 'DEPALLETIZATION' },
  { label: 'Quality Check', value: 'QUALITY_CHECK' },
  { label: 'Loading', value: 'LOADING' },
  { label: 'Unloading', value: 'UNLOADING' },
  { label: 'Cross-dock', value: 'CROSS_DOCK' },
  { label: 'Inventory Count', value: 'INVENTORY_COUNT' },
  { label: 'Rework', value: 'REWORK' },
  { label: 'Scrapping', value: 'SCRAPPING' },
  { label: 'Refurbishment', value: 'REFURBISHMENT' },
  { label: 'Destruction', value: 'DESTRUCTION' },
  { label: 'Other', value: 'OTHER' }
];

export const BILLING_METRICS = [
  ...BILLING_CHARGE_UNITS,
  ...BILLING_CHARGE_TYPES.filter(type => type.value !== 'STORAGE')
];

export function billingChargeTypeLabel(value: string | null | undefined): string {
  if (value === 'SQM' || value === 'CBM') {
    return 'Storage';
  }
  return BILLING_CHARGE_TYPES.find(type => type.value === value)?.label || value || '-';
}

export function billingChargeUnitLabel(value: string | null | undefined): string {
  return BILLING_CHARGE_UNITS.find(unit => unit.value === value)?.label || value || '-';
}

export const REFURBISHMENT_SOURCES = [
  { label: 'Internal', value: 'INTERNAL' },
  { label: 'Outsourced', value: 'OUTSOURCED' }
];

export const ACTIVE_FILTER_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' }
];

export function warehousingHomeBreadcrumb(): MenuItem {
  return { icon: 'pi pi-home', routerLink: '/' };
}

export function warehousingBreadcrumb(label: string, parentLabel?: string, parentRoute?: string): MenuItem[] {
  const items: MenuItem[] = [{ label: 'Warehousing', routerLink: '/warehousing/overview' }];
  if (parentLabel) {
    items.push({ label: parentLabel, routerLink: parentRoute });
  }
  items.push({ label });
  return items;
}

export function warehousingMasterTypeLabel(type: WarehousingMasterType): string {
  return WAREHOUSING_MASTER_TYPE_LABELS[type] || type;
}

export function warehousingMasterTypeDescription(type: WarehousingMasterType): string {
  return WAREHOUSING_MASTER_TYPE_DESCRIPTIONS[type] || 'Maintain warehousing setup values in their dedicated setup area.';
}

export function warehousingMasterTypeRoute(type: WarehousingMasterType): string {
  return WAREHOUSING_MASTER_TYPE_ROUTES[type] || '/warehousing/setup/storage-rates';
}

export function statusSeverity(status?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
  switch (status) {
    case 'ACTIVE':
    case 'AVAILABLE':
    case 'POSTED':
    case 'CONFIRMED':
    case 'DELIVERED':
      return 'success';
    case 'DESTROYED':
      return 'danger';
    case 'PLANNED':
      return 'secondary';
    case 'RESERVED':
    case 'PICKED':
    case 'STAGED':
    case 'LOADED':
    case 'UNDER_REFURBISHMENT':
    case 'DRAFT':
    case 'PENDING':
      return 'warn';
    case 'ALLOCATED':
    case 'DISPATCHED':
    case 'EXITED':
    case 'IN_TRANSIT':
    case 'PARTIALLY_RECEIVED':
      return 'info';
    case 'RECEIVED':
      return 'success';
    case 'INACTIVE':
    case 'CANCELLED':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function masterDataMap(): Record<WarehousingMasterType, WarehousingMasterData[]> {
  return {
    REGION: [],
    WAREHOUSE: [],
    ZONE: [],
    ITEM_CONDITION: [],
    TENANT_UOM: [],
    CATEGORY: [],
    BRAND: [],
    SUB_BRAND: []
  };
}

export function isThirdPartyVirtualWarehouse(row?: Pick<WarehousingMasterData, 'warehouseOwnershipType'> | null): boolean {
  return `${row?.warehouseOwnershipType || ''}`.toUpperCase() === 'THIRD_PARTY_VIRTUAL';
}

export function companyOwnedWarehouses(rows: WarehousingMasterData[] | null | undefined): WarehousingMasterData[] {
  return (rows || []).filter(row => !isThirdPartyVirtualWarehouse(row));
}

/** Warehousing reports are intentionally restricted to the tenant's own physical locations. */
export function ownPhysicalWarehouses(rows: WarehousingMasterData[] | null | undefined): WarehousingMasterData[] {
  return (rows || []).filter(row => {
    const ownership = `${row.warehouseOwnershipType || ''}`.trim().toUpperCase();
    return !ownership || ownership === 'COMPANY';
  });
}
